import { useEffect } from "react";
import { useLocation } from "wouter";
import { DEFAULT_IMAGE, getRouteMetadata } from "@/lib/routeMetadata";

function setMeta(selector: string, attribute: "name" | "property", value: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, selector.match(/["']([^"']+)["']/)?.[1] ?? "");
    document.head.appendChild(element);
  }
  element.content = value;
}

export default function RouteMetadata() {
  const [location] = useLocation();
  const { canonical, description, noindex, schema, title } = getRouteMetadata(location);

  useEffect(() => {
    document.title = title;
    let canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;
    setMeta('meta[name="description"]', "name", description);
    setMeta('meta[name="robots"]', "name", noindex ? "noindex, nofollow" : "index, follow");
    setMeta('meta[property="og:title"]', "property", title);
    setMeta('meta[property="og:description"]', "property", description);
    setMeta('meta[property="og:url"]', "property", canonical);
    setMeta('meta[property="og:image"]', "property", DEFAULT_IMAGE);
    setMeta('meta[property="og:type"]', "property", "website");
    setMeta('meta[name="twitter:card"]', "name", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", title);
    setMeta('meta[name="twitter:description"]', "name", description);
    setMeta('meta[name="twitter:image"]', "name", DEFAULT_IMAGE);

    const existing = document.head.querySelector<HTMLScriptElement>("#route-json-ld");
    if (schema) {
      const script = existing ?? document.createElement("script");
      script.id = "route-json-ld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schema);
      if (!existing) document.head.appendChild(script);
    } else {
      existing?.remove();
    }
  }, [canonical, description, noindex, schema, title]);

  return null;
}